import type { AnalyticsBase, PRIMARY_POSSESSIVE } from "./base"
import type { LessonAnalyticsCore } from "./lesson"

export type AnalyticsApp =
  | AppOnboard
  | AppCreateSession
  | AppCreateClass
  | AppUndo
  | AppImport
  | AppSearchStandard
  | AppSearchStandardEditPane
  | AppOpenModal
  | AppEditPattern

export interface AppOnboard extends AnalyticsBase {
  date_plan_scheduled_for: null

  primary_verb: "use"
  primary_possessive: "owns"

  primary_object_type: "app"
  primary_object_id: "commoncurriculum.com"
  primary_object_specifier: "planbook_onboarding" | "onboarding"
  primary_object_id_and_ancestor_ids: Array<string>

  adverbial_verb: "navigating"

  adverbial_object_type: "screen"
  adverbial_object_id: "order" | "template_evaluation" | null
  adverbial_object_specifier: null
  adverbial_object_id_and_ancestor_ids: Array<string> | null
}

export interface AppCreateSession extends AnalyticsBase {
  root_type: "app"

  primary_verb: "create"

  primary_object_type: "session"
}

export interface AppCreateClass extends AnalyticsBase {
  date_plan_scheduled_for: null

  primary_verb: "use"
  primary_possessive: "owns"

  primary_object_type: "app"
  primary_object_id: "commoncurriculum.com"
  primary_object_specifier: "course_create"
  primary_object_id_and_ancestor_ids: Array<string>

  adverbial_verb: "navigating" | "editing"

  adverbial_object_type: "screen" | "template"
  adverbial_object_id: "name" | "final_screen" | "template_chooser" | string
  adverbial_object_specifier: null
  adverbial_object_id_and_ancestor_ids: Array<string> | null
}

export interface AppUndo extends AnalyticsBase {
  date_plan_scheduled_for: string | null

  primary_verb: "use"
  primary_possessive: PRIMARY_POSSESSIVE

  primary_object_type: "app"
  primary_object_id: "commoncurriculum.com"
  primary_object_specifier: "undo" | "redo"
  primary_object_id_and_ancestor_ids: Array<string> | null

  adverbial_verb: null

  adverbial_object_type: null
  adverbial_object_id: null
  adverbial_object_specifier: null
  adverbial_object_id_and_ancestor_ids: Array<string> | null
}

export const AppUndo = {
  create(
    core: LessonAnalyticsCore,
    fields: { readonly specifier: AppUndo["primary_object_specifier"] },
  ): AppUndo {
    return {
      root_id: core.root_id,
      root_type: core.root_type,
      date_plan_scheduled_for: core.date_plan_scheduled_for,
      primary_verb: "use",
      primary_possessive: core.primary_possessive,
      primary_object_type: "app",
      primary_object_id: "commoncurriculum.com",
      primary_object_specifier: fields.specifier,
      primary_object_id_and_ancestor_ids: core.primary_object_id_and_ancestor_ids,
      adverbial_verb: null,
      adverbial_object_type: null,
      adverbial_possessive: null,
      adverbial_object_specifier: null,
      adverbial_object_id: null,
      adverbial_object_id_and_ancestor_ids: null,
    }
  },
}

export interface AppImport extends AnalyticsBase {
  date_plan_scheduled_for: null

  primary_verb: "use"
  primary_possessive: "owns"

  primary_object_type: "app"
  primary_object_id: "commoncurriculum.com"
  primary_object_specifier: "import"
  primary_object_id_and_ancestor_ids: Array<string>

  adverbial_verb: "navigating"

  adverbial_object_type: "screen"
  adverbial_object_id: "final_screen"
  adverbial_object_specifier: null
  adverbial_object_id_and_ancestor_ids: Array<string> | null
}

export interface AppSearchStandard extends AnalyticsBase {
  date_plan_scheduled_for: null

  primary_verb: "use"
  primary_possessive: "owns"

  primary_object_type: "app"
  primary_object_id: "commoncurriculum.com"
  primary_object_specifier: "standards_search"
  primary_object_id_and_ancestor_ids: Array<string>

  adverbial_verb: "navigating" | "selecting"

  adverbial_object_type: "screen" | "jurisdiction" | "subject"
  adverbial_object_id: string | "back_to_pane"
  adverbial_object_specifier: null
  adverbial_object_id_and_ancestor_ids: Array<string> | null
}
export interface AppSearchStandardEditPane extends AnalyticsBase {
  date_plan_scheduled_for: null

  primary_verb: "use"
  primary_possessive: "owns"

  primary_object_type: "app"
  primary_object_id: "commoncurriculum.com"
  primary_object_specifier: "standards_search"
  primary_object_id_and_ancestor_ids: Array<string>

  adverbial_verb: "adding" | "removing"

  adverbial_object_type: "standard_set_pane"
  adverbial_object_id: null
  adverbial_object_specifier: null
  adverbial_object_id_and_ancestor_ids: Array<string> | null
}

export interface AppOpenModal extends AnalyticsBase {
  date_plan_scheduled_for: null

  primary_verb: "use"
  primary_possessive: "owns"

  primary_object_type: "app"
  primary_object_id: "commoncurriculum.com"
  primary_object_specifier: null

  adverbial_verb: "opening"

  adverbial_object_type: "modal"
  adverbial_object_id: string
  adverbial_object_specifier: null
  adverbial_object_id_and_ancestor_ids: null
}

export interface AppEditPattern extends AnalyticsBase {
  date_plan_scheduled_for: null

  primary_verb: "edit"
  primary_possessive: "owns"

  primary_object_type: "app"
  primary_object_id: "commoncurriculum.com"
  primary_object_specifier: "dashboard"
  primary_object_id_and_ancestor_ids: Array<string> | null

  adverbial_verb: null

  adverbial_object_type: null
  adverbial_object_id: null
  adverbial_object_specifier: null
  adverbial_object_id_and_ancestor_ids: Array<string> | null
}
